<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import WorkforceBotToggle from './WorkforceBotToggle.vue';
import AgentFollowup from './AgentFollowup.vue';
import AgentHandoff from './AgentHandoff.vue';
import Avatar from 'next/avatar/Avatar.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import wootConstants from 'dashboard/constants/globals';
import ConversationApi from 'dashboard/api/inbox/conversation';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useI18n } from 'vue-i18n';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useAlert } from 'dashboard/composables';
import { conversaDeTeste } from 'dashboard/helper/conversaDeTeste';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_participating: 'participating',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

const displayContactName = computed(() => {
  const contact = currentContact.value || {};
  return (
    contact.display_name ||
    contact.name ||
    contact.phone_number ||
    contact.email ||
    contact.identifier ||
    'Visitante'
  );
});
// A etiqueta "teste" (REPORTS-04 D29), pela régua dos relatórios: a etiqueta da conversa ou o
// contato, que no store muda na hora.
const deTeste = computed(() =>
  conversaDeTeste(props.chat.labels, currentContact.value)
);
const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

const copyConversationId = async () => {
  try {
    await copyTextToClipboard(String(props.chat.id));
    useAlert(t('CONVERSATION.HEADER.COPY_ID_SUCCESS'));
  } catch (error) {
    // error
  }
};

// O modo do botão do bot para ESTA conversa (PLAN §3.4), de duas fontes lidas uma vez cada:
//
//   1. o agent bot da inbox, do store `agentBots` — carregado sob demanda, porque só a tela
//      de configuração da inbox o carregava. Sem bot: `none`, e nada é renderizado.
//   2. com bot, o `GET .../bot`: `agent: null` é bot de webhook sem PowerAgent (`bridge`, o
//      toggle de antes); qualquer outra coisa é `native`. Uma ida por conversa, guardada
//      aqui — a maioria das inboxes não tem bot, e as que têm não mudam de agente no meio
//      de uma sessão.
//
// O id do bot vem do estado do módulo, e não do getter `getActiveAgentBot`: o getter
// resolve o bot na lista `records`, que só a tela de administração carrega, e devolveria
// `{}` para quem atende — que é justamente quem precisa do botão.
const modoDoBot = ref('none');
const modoPorConversa = new Map();
const inboxesEmCarga = new Set();

// `undefined` = ainda não perguntado; `null` = perguntado, sem bot.
const agentBotDaInbox = computed(() => {
  const mapa = store.state.agentBots.agentBotInbox;
  const inboxId = Number(props.chat.inbox_id);
  if (!(inboxId in mapa)) return undefined;
  return mapa[inboxId] || null;
});

function carregarAgentBotDaInbox(inboxId) {
  if (inboxesEmCarga.has(inboxId)) return;
  inboxesEmCarga.add(inboxId);
  store
    .dispatch('agentBots/fetchAgentBotInbox', inboxId)
    .catch(() => {
      // Sem resposta, sem botão: um toggle que talvez grave uma chave que ninguém lê é
      // pior do que nenhum. A próxima conversa desta inbox tenta de novo.
    })
    .finally(() => inboxesEmCarga.delete(inboxId));
}

async function modoPelaRota(conversationId) {
  if (modoPorConversa.has(conversationId)) {
    return modoPorConversa.get(conversationId);
  }
  let modo;
  try {
    const { data } = await ConversationApi.agentBot(conversationId);
    modo = data.agent ? 'native' : 'bridge';
  } catch (e) {
    // 502 com `agent` no corpo: há PowerAgent, o runtime é que não respondeu. O botão
    // segue nativo — o espelho mostra o último estado — e o clique dirá que o runtime
    // caiu. Qualquer outro erro não conclui nada: fica o toggle antigo e não se guarda,
    // para perguntar de novo na próxima vez.
    if (!e?.response?.data?.agent) return 'bridge';
    modo = 'native';
  }
  modoPorConversa.set(conversationId, modo);
  return modo;
}

watch(
  () => [props.chat.id, props.chat.inbox_id, agentBotDaInbox.value],
  async ([conversationId, inboxId, bot]) => {
    if (!conversationId || !inboxId) {
      modoDoBot.value = 'none';
      return;
    }
    if (bot === undefined) {
      modoDoBot.value = 'none';
      carregarAgentBotDaInbox(Number(inboxId));
      return;
    }
    if (bot === null) {
      modoDoBot.value = 'none';
      return;
    }
    // Enquanto a rota responde, nenhum botão — e não o da conversa anterior, que pode ser
    // de outro modo. Com cache não há espera, e o botão não pisca.
    if (!modoPorConversa.has(conversationId)) modoDoBot.value = 'none';
    const modo = await modoPelaRota(conversationId);
    // A resposta de uma conversa que já não é a aberta não pode vestir a atual.
    if (props.chat.id === conversationId) modoDoBot.value = modo;
  },
  { immediate: true }
);
</script>

<template>
  <div
    ref="conversationHeader"
    class="flex flex-col gap-3 items-center justify-between flex-1 w-full min-w-0 xl:flex-row px-3 pt-3 pb-2 h-24 xl:h-12"
  >
    <div
      class="flex items-center justify-start w-full xl:w-auto max-w-full min-w-0 xl:flex-1"
    >
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        class="ltr:mr-2 rtl:ml-2"
      />
      <Avatar
        :name="displayContactName"
        :src="currentContact.thumbnail"
        :size="32"
        :status="currentContact.availability_status"
        hide-offline-status
        rounded-full
      />
      <div
        class="flex flex-col items-start min-w-0 ml-2 overflow-hidden rtl:ml-0 rtl:mr-2"
      >
        <div class="flex flex-row items-center max-w-full gap-1 p-0 m-0">
          <span
            class="text-sm font-medium truncate leading-tight text-n-slate-12"
          >
            {{ displayContactName }}
          </span>
          <Label
            v-if="deTeste"
            :label="{
              title: t('CONVERSATION.TEST_CONVERSATION.LABEL'),
              description: t('CONVERSATION.TEST_CONVERSATION.TOOLTIP'),
            }"
            color="amber"
            compact
          />
          <fluent-icon
            v-if="!isHMACVerified"
            v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
            size="14"
            class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
            icon="warning"
          />
        </div>

        <div
          class="flex items-center gap-1 overflow-hidden text-xs conversation--header--actions text-n-slate-11 text-ellipsis whitespace-nowrap"
        >
          <button
            type="button"
            class="truncate text-label-small text-n-slate-11 hover:text-n-slate-12 !p-0 cucursor-pointer"
            @click="copyConversationId"
          >
            {{ `#${chat.id}` }}
          </button>
          <span v-if="hasMultipleInboxes">•</span>
          <InboxName v-if="hasMultipleInboxes" :inbox="inbox" class="!mx-0" />
          <span v-if="isSnoozed">•</span>
          <span v-if="isSnoozed" class="font-medium text-n-amber-10">
            {{ snoozedDisplayText }}
          </span>
        </div>
      </div>
    </div>
    <div
      class="flex flex-row items-center justify-start xl:justify-end flex-shrink-0 gap-2 w-full xl:w-auto header-actions-wrap"
    >
      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />
      <!-- Sidepanel toggles, docked with the other actions instead of floating
           over the message list. -->
      <slot name="actions" />
      <MoreActions :conversation-id="currentChat.id" />
      <!-- O interruptor do bot, no modo desta conversa: some sem bot, é o toggle antigo
           com bot de webhook, e fala com o runtime quando há PowerAgent. -->
      <WorkforceBotToggle
        v-if="modoDoBot !== 'none'"
        :conversation-id="currentChat.id"
        :mode="modoDoBot"
      />
      <!-- Por que o bot está calado: só existe para conversa de PowerAgent, porque só o
           runtime tem o rastro. -->
      <AgentHandoff
        v-if="modoDoBot === 'native'"
        :conversation-id="currentChat.id"
      />
      <!-- O rastro do PowerAgent saiu daqui em 15/09/2026, por decisão do Murilo: ele era
           por CONVERSA, e a pergunta que se faz olhando uma resposta é sobre AQUELE turno —
           que modelo, quanta latência, que fontes, que tools. Esse painel existe por
           mensagem (`components-next/message/DebugPanel.vue`), e ter os dois deixava o
           mais útil escondido atrás do mais visível. -->
      <!-- O follow-up desta conversa. Diferente do rastro acima, aparece para QUEM ATENDE:
           é quem recebe o "não me manda mais mensagem". -->
      <AgentFollowup :conversation-id="currentChat.id" />
    </div>
  </div>
</template>
